"use client";

import { memo } from "react";
import Badge from "../ui/Badge";
import CategoryChip from "../ui/CategoryChip";
import Meta from "../ui/Meta";
import Link from "next/link";
import CustomImage from "../ui/CustomImage";
import { useAuth } from "@/contexts/AuthContext";

const CHIP_LIMIT = 2;

function parseCategoryIds(value) {
    return String(value || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
}

function orderCategoryIds(existingIds, selectedId) {
    const ordered = [];
    const seen = new Set();

    if (selectedId && existingIds.includes(selectedId)) {
        ordered.push(selectedId);
        seen.add(selectedId);
    }

    for (const id of existingIds) {
        if (seen.has(id)) continue;
        seen.add(id);
        ordered.push(id);
    }

    return ordered;
}

function parseNewsCategoryIds(data) {
    const ids = parseCategoryIds(data?.categoryIds);
    if (data?.categoryId != null && data.categoryId !== "") {
        const primary = String(data.categoryId);
        if (!ids.includes(primary)) ids.unshift(primary);
    }
    return ids;
}

function resolveChip(id, selectedCategory, categories) {
    const selectedId = selectedCategory?.id != null ? String(selectedCategory.id) : null;
    const isSelected = selectedId && id === selectedId;

    if (isSelected) {
        const name = selectedCategory.name || "";
        const slug =
            selectedCategory.slug ||
            selectedCategory.nameEnglish?.toLowerCase() ||
            "";
        return name && slug ? { name, slug } : null;
    }

    const category = categories.find((cat) => String(cat.id) === String(id));
    if (!category?.name) return null;

    const slug = category.slug || category.nameEnglish?.toLowerCase() || "";
    if (!slug) return null;

    return { name: category.name, slug };
}

const FeaturedCard = ({ headline, kicker, badge, data = null, selectedCategory = null }) => {
    const { categories } = useAuth();
    const selectedId = selectedCategory?.id != null ? String(selectedCategory.id) : null;
    const categoryIds = orderCategoryIds(parseNewsCategoryIds(data), selectedId).slice(0, CHIP_LIMIT);

    return (
        <article className="group">
            <div className="relative overflow-hidden rounded-lg">
                <Link href={`/news/${data?.slug || ''}`}>
                    <CustomImage
                        src={data.featuredImage}
                        alt={headline}
                        width={800}
                        height={450}
                        className="w-full aspect-[16/10] object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                </Link>

                {badge && <Badge type={badge} />}
            </div>

            <div className="pt-3">
                <div className="mb-2 flex flex-wrap gap-2">
                    {categoryIds.map((id) => {
                        const chip = resolveChip(id, selectedCategory, categories);
                        if (!chip) return null;

                        return (
                            <CategoryChip
                                key={id}
                                name={chip.name}
                                url={`/category/${chip.slug}`}
                            />
                        );
                    })}
                </div>

                <Link href={`/news/${data?.slug || ''}`}>
                    <h3 className="mr my-2 mb-1.5 text-lg font-bold leading-snug text-(--text-primary) line-clamp-3 group-hover:text-(--brand-primary) transition-colors">
                        {headline}
                    </h3>
                </Link>

                {kicker && (
                    <p
                        className="mr m-0 text-[13px]"
                        style={{ color: 'var(--text-tertiary)' }}
                    >
                        {kicker}
                    </p>
                )}

                <Meta data={data} />
            </div>
        </article>
    );
}

export default memo(FeaturedCard);
